import { useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Badge, type BadgeVariant } from '@/components/data-display/Badge';
import { label, type ScreeningSummary } from './types';
export const fieldClass = 'w-full rounded-md border border-[#ddd] bg-white px-3 py-2 text-sm';
export function ScreeningBadge({ status }: { status: ScreeningSummary['status'] }) {
  const variants: Record<ScreeningSummary['status'], BadgeVariant> = {
    pending: 'default',
    approved: 'success',
    hold: 'warning',
    declined: 'error',
  };
  return <Badge variant={variants[status]}>{label(status)}</Badge>;
}
export function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-lg border border-[#e8e8e8] bg-white p-4 sm:p-6">
      <h2 className="mb-4 text-base font-semibold text-[#333]">{title}</h2>
      {children}
    </section>
  );
}
export function Facts({ values }: { values: Record<string, ReactNode> }) {
  return (
    <dl className="grid gap-4 text-sm sm:grid-cols-2 xl:grid-cols-3">
      {Object.entries(values).map(([key, value]) => (
        <div className="min-w-0" key={key}>
          <dt className="mb-1 text-xs text-[#777]">{key}</dt>
          <dd className="break-words text-[#333]">
            {value === null || value === undefined || value === '' ? 'Not available' : value}
          </dd>
        </div>
      ))}
    </dl>
  );
}
export function time(value: string | null) {
  if (!value) return 'Not available';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? 'Not available'
    : date.toLocaleString('en-GB', { timeZone: 'UTC' }) + ' UTC';
}
export function Structured({ title, value }: { title: string; value: unknown }) {
  return (
    <details className="mt-3 rounded-md border border-[#eee] p-3 text-sm">
      <summary className="cursor-pointer font-medium text-[#555]">{title}</summary>
      <pre className="mt-3 max-h-96 overflow-auto whitespace-pre-wrap break-all text-xs">
        {JSON.stringify(value, null, 2)}
      </pre>
    </details>
  );
}
export function ScreeningNotice() {
  return (
    <div className="rounded-lg border border-[#ffe0b2] bg-[#fff8ee] p-4 text-sm text-[#784600]">
      Screening decisions are separate from financial processing. In shadow mode they do not
      execute, credit or reject a payment. Check the owning payment record for its financial status.
    </div>
  );
}

/** Groups a decimal string for display without converting it to a JavaScript number. */
export function formatDecimal(value: string) {
  const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(value);
  if (!match) return value;
  const [, sign, whole, fraction = ''] = match;
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const trimmed = fraction.replace(/0+$/, '').padEnd(2, '0');
  return `${sign}${grouped}.${trimmed}`;
}
export function CopyButton({ value, name }: { value: string; name: string }) {
  const [copied, setCopied] = useState<'done' | 'failed' | null>(null);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied('done');
    } catch {
      setCopied('failed');
    }
  };
  return (
    <span className="inline-flex items-center gap-2">
      <button
        type="button"
        onClick={() => void copy()}
        aria-label={`Copy ${name}`}
        className="rounded border border-[#ddd] px-2 py-0.5 text-xs text-[#555] hover:bg-[#f5f5f5] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#6B0F1A]"
      >
        Copy
      </button>
      <span role="status" className="text-xs text-[#777]">
        {copied === 'done' ? 'Copied' : copied === 'failed' ? 'Copy unavailable' : ''}
      </span>
    </span>
  );
}
export type TabItem = { id: string; label: string; content: ReactNode };
/** WAI-ARIA tabs: arrow keys, Home and End move focus; inactive panels stay in the DOM, hidden. */
export function Tabs({ label, items }: { label: string; items: TabItem[] }) {
  const [active, setActive] = useState(items[0]?.id);
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});
  const move = (event: KeyboardEvent, index: number) => {
    const last = items.length - 1;
    const next =
      event.key === 'ArrowRight'
        ? index === last
          ? 0
          : index + 1
        : event.key === 'ArrowLeft'
          ? index === 0
            ? last
            : index - 1
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? last
              : null;
    if (next === null) return;
    event.preventDefault();
    setActive(items[next].id);
    refs.current[items[next].id]?.focus();
  };
  return (
    <div className="rounded-lg border border-[#e8e8e8] bg-white">
      <div
        role="tablist"
        aria-label={label}
        className="flex gap-1 overflow-x-auto border-b border-[#eee] px-2 sm:px-4"
      >
        {items.map((item, index) => (
          <button
            key={item.id}
            ref={(el) => {
              refs.current[item.id] = el;
            }}
            id={`screening-tab-${item.id}`}
            role="tab"
            type="button"
            aria-selected={active === item.id}
            aria-controls={`screening-panel-${item.id}`}
            tabIndex={active === item.id ? 0 : -1}
            onClick={() => setActive(item.id)}
            onKeyDown={(e) => move(e, index)}
            className={`whitespace-nowrap border-b-2 px-3 py-3 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-[#6B0F1A] ${
              active === item.id
                ? 'border-[#6B0F1A] text-[#6B0F1A]'
                : 'border-transparent text-[#666] hover:text-[#333]'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>
      {items.map((item) => (
        <div
          key={item.id}
          id={`screening-panel-${item.id}`}
          role="tabpanel"
          aria-labelledby={`screening-tab-${item.id}`}
          hidden={active !== item.id}
          tabIndex={0}
          className="space-y-5 p-4 focus:outline-none sm:p-6"
        >
          {item.content}
        </div>
      ))}
    </div>
  );
}
